import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { AssistentZugang } from './AssistentZugang'

interface BenutzerAntwort {
  id: number
  email: string
  vorname: string | null
  nachname: string | null
  rollen: string[]
  aktiv: boolean
  letzterLogin: string | null
  mandantId: number
  mandantName: string | null
  appNutzer: boolean
  appDisponent: boolean
  fahrerId: number | null
  fahrerName: string | null
}

/** Fahrer des Betriebs — für die Zuordnung eines Zugangs. */
interface FahrerZeile {
  id: number
  personalnummer: string
  vorname: string
  nachname: string
}

interface AnlageErgebnis {
  benutzer: BenutzerAntwort
  einladungVersendet: boolean
  hinweis: string | null
  einladungslink: string | null
}

interface MandantAntwort {
  id: number
  name: string
  kuerzel: string
}

/** Ohne PLATTFORM_ADMIN — die Rolle vergibt ausschließlich der Plattformbetreiber. */
const ROLLEN = [
  { wert: 'ADMIN', text: 'Administrator', hinweis: 'Verwaltet Zugänge und Stammdaten des Betriebs' },
  { wert: 'DISPONENT', text: 'Disponent', hinweis: 'Plant Kurse und teilt Fahrer und Fahrzeuge zu' },
  { wert: 'PERSONAL', text: 'Personal', hinweis: 'Qualifikationen, Fristen, Arbeitszeitnachweise' },
  { wert: 'FAHRER', text: 'Fahrer', hinweis: 'Sieht nur den eigenen Dienst' },
  {
    wert: 'WERKSTATT',
    text: 'Werkstatt',
    hinweis: 'Fahrzeuge und Meldungen, eigener Schriftwechsel mit den Fahrern',
  },
  { wert: 'LESER', text: 'Leser', hinweis: 'Nur lesend, ohne personenbezogene Bewegungsdaten' },
]

const ROLLE_TEXT: Record<string, string> = {
  PLATTFORM_ADMIN: 'Plattform',
  ADMIN: 'Administrator',
  DISPONENT: 'Disponent',
  PERSONAL: 'Personal',
  FAHRER: 'Fahrer',
  WERKSTATT: 'Werkstatt',
  LESER: 'Leser',
  ASSISTENT: 'Assistent',
}

interface FormularZustand {
  id: number | null
  email: string
  vorname: string
  nachname: string
  passwort: string
  rollen: string[]
  aktiv: boolean
  mandantId: number | null
  appNutzer: boolean
  appDisponent: boolean
  /** 0 bedeutet „keine Zuordnung" — null hiesse „unverändert lassen". */
  fahrerId: number
  /**
   * Der Name des zugeordneten Fahrers — nur für die Anzeige.
   *
   * Der Plattform-Administrator hat die Fahrerliste des fremden Betriebs nicht und
   * sähe im Auswahlfeld sonst „keine Zuordnung“, obwohl eine besteht.
   */
  fahrerName: string | null
}

export function Zugaenge() {
  const { benutzer: angemeldet, hatRolle } = useAuth()
  const istPlattformAdmin = hatRolle('PLATTFORM_ADMIN')
  // Zugaenge eines Betriebs vergibt ausschliesslich dessen eigener Administrator.
  const darfAnlegen = hatRolle('ADMIN')

  const [benutzer, setBenutzer] = useState<BenutzerAntwort[]>([])
  const [mandanten, setMandanten] = useState<MandantAntwort[]>([])
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [formular, setFormular] = useState<FormularZustand | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const [einladung, setEinladung] = useState<AnlageErgebnis | null>(null)
  const [fahrer, setFahrer] = useState<FahrerZeile[]>([])

  async function laden() {
    setLaedt(true)
    try {
      setBenutzer(await api.get<BenutzerAntwort[]>('/api/benutzer'))
      if (istPlattformAdmin) {
        setMandanten(await api.get<MandantAntwort[]>('/api/mandanten'))
      } else {
        // Der Plattform-Administrator hat kein eigenes Fahrpersonal; für alle anderen
        // ist die Liste die Grundlage der Zuordnung.
        setFahrer(await api.get<FahrerZeile[]>('/api/fahrpersonal'))
      }
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [istPlattformAdmin])

  function neu() {
    setFormular({
      id: null,
      email: '',
      vorname: '',
      nachname: '',
      passwort: '',
      rollen: ['DISPONENT'],
      aktiv: true,
      appNutzer: false,
      appDisponent: false,
      fahrerId: 0,
      fahrerName: null,
      mandantId: angemeldet?.mandantId ?? null,
    })
  }

  function bearbeite(b: BenutzerAntwort) {
    setFormular({
      id: b.id,
      email: b.email,
      vorname: b.vorname ?? '',
      nachname: b.nachname ?? '',
      passwort: '',
      rollen: b.rollen.filter((r) => r !== 'PLATTFORM_ADMIN'),
      aktiv: b.aktiv,
      appNutzer: b.appNutzer,
      appDisponent: b.appDisponent,
      fahrerId: b.fahrerId ?? 0,
      fahrerName: b.fahrerName,
      mandantId: b.mandantId,
    })
  }

  async function speichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (!formular) return
    setSpeichert(true)
    setMeldung(null)
    try {
      const koerper = {
        email: formular.email,
        vorname: formular.vorname || null,
        nachname: formular.nachname || null,
        passwort: formular.passwort || null,
        rollen: formular.rollen,
        aktiv: formular.aktiv,
        mandantId: formular.mandantId,
        appNutzer: formular.appNutzer,
        appDisponent: formular.appDisponent,
        fahrerId: formular.fahrerId,
      }
      if (formular.id === null) {
        const ergebnis = await api.post<AnlageErgebnis>('/api/benutzer', koerper)
        if (ergebnis.einladungVersendet) {
          setMeldung({ art: 'erfolg', text: `Einladung an ${formular.email} versendet` })
        } else {
          setEinladung(ergebnis)
          setMeldung({
            art: 'hinweis',
            text: `Zugang angelegt, aber keine Mail versendet: ${ergebnis.hinweis ?? 'unbekannter Grund'}`,
          })
        }
      } else {
        await api.put(`/api/benutzer/${formular.id}`, koerper)
        setMeldung({ art: 'erfolg', text: 'Änderungen gespeichert' })
      }
      setFormular(null)
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen' })
    } finally {
      setSpeichert(false)
    }
  }

  async function einladungErneut(b: BenutzerAntwort) {
    setMeldung(null)
    try {
      const ergebnis = await api.post<AnlageErgebnis>(`/api/benutzer/${b.id}/einladung`)
      if (ergebnis.einladungVersendet) {
        setMeldung({ art: 'erfolg', text: `Neue Einladung an ${b.email} versendet` })
      } else {
        setEinladung(ergebnis)
        setMeldung({
          art: 'hinweis',
          text: `Keine Mail versendet: ${ergebnis.hinweis ?? 'unbekannter Grund'}`,
        })
      }
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Versand fehlgeschlagen' })
    }
  }

  async function sperren(b: BenutzerAntwort) {
    if (!confirm(`Zugang „${b.email}" sperren?`)) return
    try {
      await api.loeschen(`/api/benutzer/${b.id}`)
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Sperren fehlgeschlagen' })
    }
  }

  // Derselbe Schalter oeffnet je nach Rolle eine andere Ansicht: dem Disponenten die
  // Disposition, der Werkstatt ihre Fahrzeuge und Meldungen. Wer beides traegt, gilt als
  // Disposition — sie darf ohnehin alles sehen.
  const nurWerkstatt =
    !!formular &&
    formular.rollen.includes('WERKSTATT') &&
    !formular.rollen.some((r) => ['ADMIN', 'DISPONENT', 'PERSONAL'].includes(r))

  function schalteRolle(rolle: string) {
    if (!formular) return
    const enthalten = formular.rollen.includes(rolle)
    setFormular({
      ...formular,
      rollen: enthalten ? formular.rollen.filter((r) => r !== rolle) : [...formular.rollen, rolle],
    })
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Zugänge</h1>
          <p>
            {istPlattformAdmin
              ? 'Alle Benutzer der Plattform, nach Mandant getrennt.'
              : `Benutzer von ${angemeldet?.mandantName ?? 'Ihrem Betrieb'}.`}
          </p>
        </div>
        {!formular && darfAnlegen && (
          <button className="knopf-akzent" onClick={neu}>
            Zugang anlegen
          </button>
        )}
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {/* Ohne Mailversand bleibt der Link sonst unerreichbar — er existiert nur einmal. */}
      {einladung?.einladungslink && (
        <div className="karte abschnitt" style={{ borderLeft: '4px solid var(--orange-500)' }}>
          <h2>Einladungslink für {einladung.benutzer.email}</h2>
          <p style={{ color: 'var(--grau-700)', fontSize: '0.88rem' }}>
            Es wurde keine E-Mail versendet. Geben Sie diesen Link weiter — er wird nicht erneut
            angezeigt.
          </p>
          <div className="protokoll">
            {einladung.einladungslink}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button
              className="knopf-schlicht"
              onClick={() => void navigator.clipboard.writeText(einladung.einladungslink!)}
            >
              Link kopieren
            </button>
            <button className="knopf-schlicht" onClick={() => setEinladung(null)}>
              Ausblenden
            </button>
          </div>
        </div>
      )}

      {formular && (
        <div className="karte abschnitt">
          <h2>{formular.id === null ? 'Neuer Zugang' : 'Zugang bearbeiten'}</h2>
          {formular.id === null && (
            <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 6 }}>
              Der neue Benutzer erhält eine Einladung per E-Mail und vergibt sein Passwort selbst.
            </p>
          )}
          <form onSubmit={speichern}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div className="feld">
                <label htmlFor="email">E-Mail *</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={formular.email}
                  onChange={(e) => setFormular({ ...formular, email: e.target.value })}
                />
              </div>
              {/* Beim Anlegen wird kein Passwort vergeben — der neue Benutzer
                  setzt es selbst über den Einladungslink. */}
              {formular.id !== null && (
                <div className="feld">
                  <label htmlFor="passwort">Passwort (leer lassen = unverändert)</label>
                  <input
                    id="passwort"
                    type="password"
                    minLength={formular.passwort ? 10 : undefined}
                    value={formular.passwort}
                    onChange={(e) => setFormular({ ...formular, passwort: e.target.value })}
                  />
                </div>
              )}
              <div className="feld">
                <label htmlFor="vorname">Vorname</label>
                <input
                  id="vorname"
                  type="text"
                  value={formular.vorname}
                  onChange={(e) => setFormular({ ...formular, vorname: e.target.value })}
                />
              </div>
              <div className="feld">
                <label htmlFor="nachname">Nachname</label>
                <input
                  id="nachname"
                  type="text"
                  value={formular.nachname}
                  onChange={(e) => setFormular({ ...formular, nachname: e.target.value })}
                />
              </div>
              {istPlattformAdmin && formular.id === null && (
                <div className="feld">
                  <label htmlFor="mandant">Mandant</label>
                  <select
                    id="mandant"
                    value={formular.mandantId ?? ''}
                    onChange={(e) => setFormular({ ...formular, mandantId: Number(e.target.value) })}
                  >
                    {mandanten.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.kuerzel})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="feld">
                <label htmlFor="aktiv">Status</label>
                <select
                  id="aktiv"
                  value={formular.aktiv ? 'ja' : 'nein'}
                  onChange={(e) => setFormular({ ...formular, aktiv: e.target.value === 'ja' })}
                >
                  <option value="ja">aktiv</option>
                  <option value="nein">gesperrt</option>
                </select>
              </div>
            </div>

            <div className="feld">
              <label>Rollen</label>
              <div style={{ display: 'grid', gap: 6 }}>
                {ROLLEN.map((r) => (
                  <label
                    key={r.wert}
                    style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontWeight: 400, cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      style={{ width: 'auto', marginTop: 3 }}
                      checked={formular.rollen.includes(r.wert)}
                      onChange={() => schalteRolle(r.wert)}
                    />
                    <span>
                      <strong style={{ fontSize: '0.88rem' }}>{r.text}</strong>
                      <span style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginLeft: 8 }}>
                        {r.hinweis}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Fahrer-App. Getrennt von den Rollen, weil es eine Frage der Ausstattung
                ist und nicht der Befugnis: Die Rolle sagt, was jemand darf, der Schalter
                nur, ob er es auch auf dem Handy tun kann. */}
            <div className="feld" style={{ marginTop: 6 }}>
              <label>Fahrer-App</label>
              <div style={{ display: 'grid', gap: 8 }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto', marginTop: 3 }}
                    checked={formular.appNutzer}
                    onChange={(e) =>
                      setFormular({
                        ...formular,
                        appNutzer: e.target.checked,
                        // Ohne App-Zugang ergibt die Dispositionsansicht keinen Sinn.
                        appDisponent: e.target.checked && formular.appDisponent,
                      })
                    }
                  />
                  <span>
                    <strong style={{ fontSize: '0.88rem' }}>App-Nutzer</strong>
                    <span style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginLeft: 8 }}>
                      darf die Fahrer-App öffnen
                    </span>
                  </span>
                </label>

                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto', marginTop: 3 }}
                    disabled={!formular.appNutzer}
                    checked={formular.appDisponent}
                    onChange={(e) =>
                      setFormular({ ...formular, appDisponent: e.target.checked })
                    }
                  />
                  <span>
                    <strong style={{ fontSize: '0.88rem' }}>
                      {nurWerkstatt ? 'App-Werkstatt' : 'App-Disponent'}
                    </strong>
                    <span style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginLeft: 8 }}>
                      {nurWerkstatt
                        ? 'sieht dort die Werkstattansicht statt des eigenen Dienstes'
                        : 'sieht dort zusätzlich die Dispositionsansicht'}
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="feld">
              <label htmlFor="fahrer">Gehört zu Fahrer</label>
              {istPlattformAdmin ? (
                // Der Betreiber lädt kein Fahrpersonal — ein leeres Auswahlfeld würde hier
                // „keine Zuordnung“ behaupten, obwohl eine besteht.
                <p style={{ margin: '4px 0' }}>
                  {formular.fahrerName ?? '— keine Zuordnung —'}
                </p>
              ) : (
                <select
                  id="fahrer"
                  value={formular.fahrerId}
                  onChange={(e) =>
                    setFormular({ ...formular, fahrerId: Number(e.target.value) })
                  }
                >
                  <option value={0}>— keine Zuordnung —</option>
                  {fahrer.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.personalnummer} · {f.nachname}, {f.vorname}
                    </option>
                  ))}
                </select>
              )}
              <small style={{ color: 'var(--grau-500)' }}>
                {istPlattformAdmin
                  ? 'Die Zuordnung ändert der Administrator des Betriebs — hier fehlt dessen Fahrerliste.'
                  : 'Nur mit Zuordnung sieht der Anmeldende in der App seinen eigenen Dienstplan. Beim Erzeugen eines QR-Codes wird sie automatisch gesetzt.'}
              </small>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="knopf-primaer" disabled={speichert}>
                {speichert ? 'Wird gespeichert …' : 'Speichern'}
              </button>
              <button type="button" className="knopf-schlicht" onClick={() => setFormular(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : benutzer.length === 0 ? (
          <div className="leer">Noch keine Zugänge vorhanden.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>E-Mail</th>
                <th>Name</th>
                {istPlattformAdmin && <th>Mandant</th>}
                <th>Rollen</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {benutzer.map((b) => (
                <tr key={b.id}>
                  <td>
                    <strong>{b.email}</strong>
                    {b.id === angemeldet?.id && (
                      <span className="etikett navy" style={{ marginLeft: 8 }}>
                        Sie
                      </span>
                    )}
                  </td>
                  <td>{[b.vorname, b.nachname].filter(Boolean).join(' ') || '–'}</td>
                  {istPlattformAdmin && <td>{b.mandantName ?? '–'}</td>}
                  <td>
                    {b.rollen.map((r) => (
                      <span
                        key={r}
                        className={`etikett ${r === 'PLATTFORM_ADMIN' ? 'orange' : 'grau'}`}
                        style={{ marginRight: 4 }}
                      >
                        {ROLLE_TEXT[r] ?? r}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span className={`etikett ${b.aktiv ? 'gruen' : 'rot'}`}>
                      {b.aktiv ? 'aktiv' : 'gesperrt'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {darfAnlegen && (
                      <>
                        <button
                          className="knopf-schlicht"
                          style={{ padding: '5px 12px' }}
                          onClick={() => void einladungErneut(b)}
                          title="Erzeugt einen neuen Einladungslink und entwertet den alten"
                        >
                          Einladung senden
                        </button>{' '}
                      </>
                    )}
                    <button
                      className="knopf-schlicht"
                      style={{ padding: '5px 12px' }}
                      onClick={() => bearbeite(b)}
                    >
                      Bearbeiten
                    </button>{' '}
                    {b.aktiv && b.id !== angemeldet?.id && (
                      <button
                        className="knopf-schlicht"
                        style={{ padding: '5px 12px', color: 'var(--rot-500)' }}
                        onClick={() => void sperren(b)}
                      >
                        Sperren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {darfAnlegen && !istPlattformAdmin && <AssistentZugang />}
    </>
  )
}
